import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./ResultadosBusqueda.css";

const ResultadosBusqueda = () => {
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const busqueda = params.get("busqueda") || "";
  const productoId = params.get("producto") || null;

  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  /* ============================================================
     CARGAR RESULTADOS
  ============================================================ */

  useEffect(() => {
    const fetchDatos = async () => {
      if (!busqueda.trim()) {
        setProductos([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await axios.get(
          `https://gj-frio-industrial-api.onrender.com/buscar?q=${encodeURIComponent(
            busqueda
          )}`
        );

        setProductos(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (error) {
        console.error(
          "Error al obtener productos:",
          error
        );

        setProductos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [busqueda]);

  /* ============================================================
     SCROLL
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [busqueda, productoId]);

  /* ============================================================
     FILTRADO ADICIONAL
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    if (!busqueda.trim()) {
      return [];
    }

    const palabras = normalizarTexto(
      busqueda
    )
      .split(/\s+/)
      .filter(Boolean);

    return productos.filter((producto) => {
      const textoCompleto =
        normalizarTexto(
          `${producto?.nombre || ""} ${
            producto?.marca || ""
          } ${producto?.modelo || ""} ${
            producto?.codigoProducto || ""
          } ${producto?.descripcion || ""}`
        );

      return palabras.every(
        (palabra) =>
          textoCompleto.includes(
            palabra
          )
      );
    });
  }, [productos, busqueda]);

  /* ============================================================
     PRODUCTO PRINCIPAL
  ============================================================ */

  const productoPrincipal = useMemo(() => {
    if (!productoId) {
      return null;
    }

    return (
      productosFiltrados.find(
        (producto) =>
          producto._id === productoId
      ) || null
    );
  }, [
    productosFiltrados,
    productoId,
  ]);

  /* ============================================================
     RELACIONADOS
  ============================================================ */

  const relacionados = useMemo(() => {
    if (!productoId) {
      return productosFiltrados;
    }

    return productosFiltrados.filter(
      (producto) =>
        producto._id !== productoId
    );
  }, [
    productosFiltrados,
    productoId,
  ]);

  const totalResultados =
    productosFiltrados.length;

  return (
    <div className="resultados-page">
      <Navbar />

      <main className="resultados-main">
        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="resultados-hero">
          <div className="resultados-container">
            <div className="resultados-hero-card">
              <div className="resultados-hero-content">
                <span className="resultados-kicker">
                  CATÁLOGO G&amp;J FRÍO
                </span>

                <h1>
                  Resultados de búsqueda
                </h1>

                <p>
                  Productos relacionados con tu
                  búsqueda en nuestro catálogo.
                </p>
              </div>

              <div className="resultados-hero-icon">
                <i className="bi bi-search"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            RESUMEN DE BÚSQUEDA
        ===================================================== */}
        <section className="resultados-summary-section">
          <div className="resultados-container">
            <div className="resultados-summary">
              <div className="resultados-search-info">
                <div className="resultados-search-icon">
                  <i className="bi bi-search"></i>
                </div>

                <div>
                  <span>
                    Buscando
                  </span>

                  <strong>
                    {busqueda || "Sin término de búsqueda"}
                  </strong>
                </div>
              </div>

              {!loading && (
                <div className="resultados-count">
                  <strong>
                    {totalResultados}
                  </strong>

                  <span>
                    {totalResultados === 1
                      ? "resultado"
                      : "resultados"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* =====================================================
            CONTENIDO
        ===================================================== */}
        <section className="resultados-content">
          <div className="resultados-container">
            {loading ? (
              <div className="resultados-loading">
                <div
                  className="spinner-border text-primary"
                  role="status"
                >
                  <span className="visually-hidden">
                    Cargando...
                  </span>
                </div>

                <strong>
                  Buscando productos
                </strong>

                <span>
                  Estamos revisando nuestro catálogo.
                </span>
              </div>
            ) : (
              <>
                {/* =============================================
                    PRODUCTO SELECCIONADO
                ============================================= */}
                {productoPrincipal && (
                  <section className="resultado-selected-section">
                    <div className="resultado-section-header">
                      <div>
                        <span className="resultado-section-kicker">
                          SELECCIONADO
                        </span>

                        <h2>
                          Producto seleccionado
                        </h2>

                        <p>
                          Este es el producto que
                          elegiste desde los resultados.
                        </p>
                      </div>

                      <span className="resultado-selected-badge">
                        <i className="bi bi-check-circle"></i>
                        Seleccionado
                      </span>
                    </div>

                    <div className="resultado-selected-wrapper">
                      <IndustrialProduct
                        img={
                          productoPrincipal.imagen
                        }
                        title={
                          productoPrincipal.nombre
                        }
                        id={
                          productoPrincipal._id
                        }
                        details={[
                          ...(productoPrincipal.marca
                            ? [
                                {
                                  label:
                                    "Marca",
                                  value:
                                    productoPrincipal.marca,
                                },
                              ]
                            : []),

                          ...(productoPrincipal.modelo
                            ? [
                                {
                                  label:
                                    "Modelo",
                                  value:
                                    productoPrincipal.modelo,
                                },
                              ]
                            : []),

                          {
                            label:
                              "Código Producto",
                            value:
                              productoPrincipal.codigoProducto,
                          },

                          {
                            label:
                              "Descripción",
                            value:
                              productoPrincipal.descripcion,
                          },
                        ]}
                        clickable={false}
                      />
                    </div>
                  </section>
                )}

                {/* =============================================
                    RELACIONADOS
                ============================================= */}
                {relacionados.length > 0 && (
                  <section className="resultado-related-section">
                    <div className="resultado-section-header">
                      <div>
                        <span className="resultado-section-kicker">
                          CATÁLOGO
                        </span>

                        <h2>
                          {productoPrincipal
                            ? "Productos relacionados"
                            : "Productos encontrados"}
                        </h2>

                        <p>
                          {productoPrincipal
                            ? "Otros productos que coinciden con tu búsqueda."
                            : "Estos productos coinciden con el término ingresado."}
                        </p>
                      </div>

                      <div className="resultado-related-count">
                        {relacionados.length}
                      </div>
                    </div>

                    <div className="resultado-products-grid">
                      {relacionados.map(
                        (item) => (
                          <IndustrialProduct
                            key={item._id}
                            img={item.imagen}
                            title={item.nombre}
                            id={item._id}
                            details={[
                              ...(item.marca
                                ? [
                                    {
                                      label:
                                        "Marca",
                                      value:
                                        item.marca,
                                    },
                                  ]
                                : []),

                              ...(item.modelo
                                ? [
                                    {
                                      label:
                                        "Modelo",
                                      value:
                                        item.modelo,
                                    },
                                  ]
                                : []),

                              {
                                label:
                                  "Código Producto",
                                value:
                                  item.codigoProducto,
                              },

                              {
                                label:
                                  "Descripción",
                                value:
                                  item.descripcion,
                              },
                            ]}
                            clickable={true}
                            busqueda={
                              busqueda
                            }
                          />
                        )
                      )}
                    </div>
                  </section>
                )}

                {/* =============================================
                    SIN RESULTADOS
                ============================================= */}
                {totalResultados === 0 && (
                  <div className="resultados-empty">
                    <div className="resultados-empty-icon">
                      <i className="bi bi-search"></i>
                    </div>

                    <h2>
                      No encontramos productos
                    </h2>

                    <p>
                      No existen productos relacionados
                      con{" "}
                      <strong>
                        “{busqueda}”
                      </strong>
                      .
                    </p>

                    <span>
                      Intenta buscar con el nombre,
                      marca, modelo o código del
                      producto.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
};

export default ResultadosBusqueda;